<template>
  <div class="app-container">
    <!-- 应用主体内容 -->
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <keep-alive :include="keepAliveComponents">
          <component :is="Component" />
        </keep-alive>
      </transition>
    </router-view>
    
    <!-- 全局组件 -->
    <GlobalNotification />
    <GlobalLoading />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import GlobalNotification from '@/components/common/GlobalNotification.vue'
import GlobalLoading from '@/components/common/GlobalLoading.vue'

// 状态管理
const appStore = ref<ReturnType<typeof useAppStore> | null>(null)
const userStore = useUserStore()
const keepAliveComponents = ref<string[]>([])

// 初始化应用
onMounted(async () => {
  try {
    // 初始化应用状态
    appStore.value = useAppStore()
    await appStore.value.initializeApp()
    
    // 初始化用户状态
    await userStore.initializeUser()
    
    // 设置初始语言
    updateDocumentLanguage(appStore.value.language)
    
    // 初始化需要缓存的组件
    keepAliveComponents.value = appStore.value.keepAliveComponents || []
    
    // 添加事件监听器
    setupEventListeners()
    
    // 性能监控初始化
    if (import.meta.env.PROD) {
      initializePerformanceMonitoring()
    }
  } catch (error) {
    console.error('App initialization failed:', error)
    // 显示错误提示
    appStore.value?.addNotification({
      type: 'error',
      title: '初始化失败',
      message: '应用初始化失败，请刷新页面重试',
      duration: 5000
    })
  }
})

// 清理资源
onUnmounted(() => {
  if (appStore.value) {
    window.removeEventListener('resize', appStore.value.handleResize)
    document.removeEventListener('visibilitychange', appStore.value.handleVisibilityChange)
  }
})

// 工具函数
const updateDocumentLanguage = (language: string) => {
  if (language) {
    document.documentElement.lang = language
  }
}

const setupEventListeners = () => {
  if (!appStore.value) return
  
  // 监听窗口变化
  window.addEventListener('resize', appStore.value.handleResize)
  
  // 监听页面可见性变化
  document.addEventListener('visibilitychange', appStore.value.handleVisibilityChange)
  
  // 监听网络状态变化
  window.addEventListener('online', handleNetworkChange)
  window.addEventListener('offline', handleNetworkChange)
  
  // 监听未捕获的错误
  window.addEventListener('error', handleUncaughtError)
  window.addEventListener('unhandledrejection', handlePromiseRejection)
}

const handleNetworkChange = () => {
  if (!appStore.value) return
  const isOnline = navigator.onLine
  appStore.value.setNetworkStatus(isOnline)
}

const handleUncaughtError = (event: ErrorEvent) => {
  console.error('Uncaught error:', event.error)
  // 这里可以添加错误上报逻辑
}

const handlePromiseRejection = (event: PromiseRejectionEvent) => {
  console.error('Unhandled promise rejection:', event.reason)
  // 这里可以添加错误上报逻辑
}

const initializePerformanceMonitoring = () => {
  // 简单的性能监控初始化
  // 在生产环境中，可以集成更复杂的性能监控工具
  const startTime = performance.now()
  
  window.addEventListener('load', () => {
    const loadTime = performance.now() - startTime
    console.log(`App loaded in ${loadTime.toFixed(2)}ms`)
    // 这里可以上报加载时间
  })
}

// 监听语言变化
watch(() => appStore.value?.language, (newLang) => {
  if (newLang) {
    updateDocumentLanguage(newLang)
  }
}, { immediate: true })

// 监听缓存组件变化
watch(() => appStore.value?.keepAliveComponents, (newComponents) => {
  if (newComponents) {
    keepAliveComponents.value = newComponents
  }
}, { deep: true })
</script>

<style lang="scss">
// Removed duplicate import to prevent style fetch aborts
// @import '@/styles/index.scss';

.app-container {
  width: 100%;
  /* 允许页面根据内容高度滚动 */
  min-height: 100vh;
  /* 仅隐藏横向滚动，纵向可滚动 */
  overflow-x: hidden;
  overflow-y: auto;
  background-color: $bg-color-page;
  position: relative;
  
  // 优化子元素的字体渲染
  * {
    font-synthesis: none;
    text-rendering: optimizeLegibility;
  }
}

// 全局过渡动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-duration-base;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform $transition-duration-base;
}

.slide-left-enter-from {
  transform: translateX(-100%);
}

.slide-left-leave-to {
  transform: translateX(100%);
}

// 页面首次加载动画
.page-enter-active {
  transition: all $transition-duration-slow;
}

.page-leave-active {
  transition: all $transition-duration-fast;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

// 防止文本选择时的高亮样式影响UI
::selection {
  background-color: rgba($primary-color, 0.2);
  color: $text-primary;
}

::-moz-selection {
  background-color: rgba($primary-color, 0.2);
  color: $text-primary;
}

// 优化焦点样式
*:focus-visible {
  outline: 2px solid $primary-color;
  outline-offset: 2px;
}

// 禁用iOS的双击缩放
* {
  touch-action: manipulation;
}

// 修复一些浏览器特定的问题
@supports (-webkit-overflow-scrolling: touch) {
  .app-container {
    -webkit-overflow-scrolling: touch;
  }
}
</style>